import React from 'react';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';

interface PieChartProps {
  /** Chart data */
  data: Array<{ name: string; value: number }>;
  /** Chart title */
  title?: string;
  /** Chart height in pixels */
  height?: number;
  /** Custom className */
  className?: string;
  /** Custom colors for segments */
  colors?: string[];
}

const DEFAULT_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

/**
 * PieChart wrapper component for proportional data
 *
 * @example
 * ```tsx
 * <PieChart
 *   data={[
 *     { name: 'Human', value: 890 },
 *     { name: 'Bot', value: 344 }
 *   ]}
 *   title="Traffic Distribution"
 *   colors={['#10b981', '#ef4444']}
 * />
 * ```
 */
export const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  height = 300,
  className,
  colors = DEFAULT_COLORS,
}) => {
  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200',
          className
        )}
        style={{ height }}
      >
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      {title && <h3 className="text-lg font-semibold mb-4">{title}</h3>}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsPieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) =>
              `${name}: ${(percent * 100).toFixed(0)}%`
            }
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
              />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </RechartsPieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PieChart;
